import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, ArrowRight, Clock, Euro } from "lucide-react";
import { DIAGNOSTIC, scrollToId } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];
const devices = Object.keys(DIAGNOSTIC);

const selectCls =
  "w-full bg-paper border border-line rounded-xl px-4 py-3.5 font-body text-sm text-ink transition-[border-color,box-shadow] duration-300 appearance-none cursor-pointer";

export const Diagnostic = ({ onPrefill }) => {
  const [device, setDevice] = useState("");
  const [problem, setProblem] = useState("");
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!device || !problem) return;
    setResult({ device, problem, ...DIAGNOSTIC[device][problem] });
  };

  const sendPrefilled = () => {
    if (!result) return;
    onPrefill(
      `Richiesta dal simulatore del sito.\nDispositivo: ${result.device}\nProblema: ${result.problem}\nStima indicativa: ${result.prezzo} — tempi: ${result.tempo}.\n\nDettagli aggiuntivi: `
    );
    scrollToId("contatti");
  };

  return (
    <section id="preventivo" data-testid="diagnostic-section" className="relative py-20 sm:py-28 bg-night grain overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-2xl mb-14"
        >
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-copper mb-5 flex items-center gap-3">
            <span className="w-8 h-px bg-copper inline-block" /> Simulatore di intervento
          </p>
          <h2 className="font-display font-bold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl text-paper">
            Due click e sai già <span className="text-copper">costi e tempi indicativi.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-paper/60 leading-relaxed">
            Seleziona dispositivo e problema: ricevi una stima trasparente, senza impegno.
            Il preventivo definitivo si conferma sempre dopo una diagnosi gratuita.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="bg-paper/[0.04] border border-paper/10 rounded-2xl p-6 sm:p-10 backdrop-blur-sm"
        >
          <div className="grid md:grid-cols-3 gap-5 items-end">
            <div>
              <label htmlFor="diagnostic-device" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-paper/55 mb-2.5">
                1 · Tipo di dispositivo
              </label>
              <select
                id="diagnostic-device"
                data-testid="diagnostic-device-select"
                value={device}
                onChange={(e) => { setDevice(e.target.value); setProblem(""); setResult(null); }}
                className={selectCls}
              >
                <option value="">Seleziona…</option>
                {devices.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="diagnostic-problem" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-paper/55 mb-2.5">
                2 · Problema riscontrato
              </label>
              <select
                id="diagnostic-problem"
                data-testid="diagnostic-problem-select"
                value={problem}
                onChange={(e) => { setProblem(e.target.value); setResult(null); }}
                disabled={!device}
                className={`${selectCls} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <option value="">{device ? "Seleziona…" : "Prima il dispositivo"}</option>
                {device && Object.keys(DIAGNOSTIC[device]).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <button
              data-testid="diagnostic-calculate-button"
              onClick={calculate}
              disabled={!device || !problem}
              className="flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-copper text-white font-display font-semibold text-sm hover:bg-copper-dark transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Calculator size={16} /> Calcola la stima
            </button>
          </div>

          <AnimatePresence>
            {result && (
              <motion.div
                data-testid="diagnostic-result"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="overflow-hidden"
              >
                <div className="mt-8 pt-8 border-t border-paper/10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
                  <div className="flex flex-wrap gap-x-10 gap-y-4">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-lg bg-copper/15 flex items-center justify-center"><Euro size={17} className="text-copper" /></span>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-paper/50">Costo indicativo</p>
                        <p data-testid="diagnostic-result-price" className="font-display font-bold text-xl text-paper">{result.prezzo}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-lg bg-copper/15 flex items-center justify-center"><Clock size={17} className="text-copper" /></span>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-paper/50">Tempi stimati</p>
                        <p data-testid="diagnostic-result-time" className="font-display font-bold text-xl text-paper">{result.tempo}</p>
                      </div>
                    </div>
                  </div>
                  <button
                    data-testid="diagnostic-send-prefilled-button"
                    onClick={sendPrefilled}
                    className="group flex items-center gap-2.5 px-6 py-3.5 rounded-xl border border-copper text-copper font-display font-semibold text-sm hover:bg-copper hover:text-white transition-colors duration-300"
                  >
                    Invia richiesta pre-compilata
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
                <p className="mt-4 font-mono text-[11px] text-paper/40 tracking-wide">
                  * Stima indicativa e non vincolante: la conferma arriva dopo la diagnosi gratuita.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
